import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";
import { Toaster } from "@/components/ui/sonner";
import { setDetectedSubdomain } from "@/hooks/useStoreSubdomain";
import PlatformBrandingEffect from "@/components/PlatformBrandingEffect";

// Subdomain / custom-domain detection for tenant stores.
// Returns the store identifier the backend resolves against:
//   - platform subdomain  → "demo-commerce"  (from demo-commerce.tokomoe.com)
//   - custom domain        → "sellmoo.id"     (from sellmoo.id or www.sellmoo.id)
//   - main platform site   → null             (render landing/app)
// The backend store endpoints match this value against BOTH `subdomain` and
// `custom_domain`, so a custom domain "just works" once its DNS points here.
const MAIN_DOMAIN = "tokomoe.com";
function getSubdomainFromHost() {
  const hostname = window.location.hostname;
  if (hostname === MAIN_DOMAIN || hostname === "www." + MAIN_DOMAIN || hostname === "localhost" || hostname === "127.0.0.1") return null;
  if (hostname.endsWith("." + MAIN_DOMAIN)) {
    return hostname.replace("." + MAIN_DOMAIN, "");
  }
  // Custom domain — use the bare host (strip leading www.) as the store identifier.
  return hostname.replace(/^www\./, "");
}
const DETECTED_SUBDOMAIN = getSubdomainFromHost();
setDetectedSubdomain(DETECTED_SUBDOMAIN);

// Layouts
import AdminLayout from "@/components/layouts/AdminLayout";
import TenantLayout from "@/components/layouts/TenantLayout";
import CustomerLayout from "@/components/layouts/CustomerLayout";

// Public Pages
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Status from "@/pages/Status";
import Help from "@/pages/Help";

// Public Store Pages
import PublicStorefront from "@/pages/public/Storefront";
import TemplatePreview from "@/pages/public/TemplatePreview";
import PublicPage from "@/pages/public/PublicPage";
import PublicProductDetail from "@/pages/public/ProductDetail";
import PublicCheckout from "@/pages/public/Checkout";
import PublicCourseDetail from "@/pages/public/CourseDetail";
import PublicDonation from "@/pages/public/Donation";
import PublicThankYou from "@/pages/public/ThankYou";
import CertificateVerify from "@/pages/public/CertificateVerify";
import StoreLogin from "@/pages/public/StoreLogin";
import StoreRegister from "@/pages/public/StoreRegister";
import PublicBlog from "@/pages/public/Blog";
import PublicFAQ from "@/pages/public/FAQ";

// Admin Pages
import AdminDashboard from "@/pages/admin/Dashboard";
import AdminTenants from "@/pages/admin/Tenants";
import AdminPackages from "@/pages/admin/Packages";
import AdminBilling from "@/pages/admin/SaaSBilling";
import AdminAffiliates from "@/pages/admin/AffiliatesSaaS";
import AdminReports from "@/pages/admin/Reports";
import AdminBillingSettings from "@/pages/admin/BillingSettings";
import AdminGlobalSettings from "@/pages/admin/GlobalSettings";
import AdminStoreTemplates from "@/pages/admin/StoreTemplates";
import AdminNotificationTemplates from "@/pages/admin/NotificationTemplates";
import AdminModuleAdoption from "@/pages/admin/ModuleAdoption";
import AdminWANotifications from "@/pages/admin/WANotifications";
import AdminAccountSettings from "@/pages/admin/AccountSettings";
import AdminFAQManager from "@/pages/admin/FAQManager";
import AdminLandingFeatures from "@/pages/admin/LandingFeatures";
import AdminTestimonials from "@/pages/admin/Testimonials";

// Tenant Pages
import TenantDashboard from "@/pages/tenant/Dashboard";
import TenantProducts from "@/pages/tenant/Products";
import TenantOrders from "@/pages/tenant/Orders";
import TenantCustomers from "@/pages/tenant/Customers";
import TenantInvoices from "@/pages/tenant/Invoices";
import TenantPaymentPlans from "@/pages/tenant/PaymentPlans";
import TenantCourses from "@/pages/tenant/Courses";
import TenantCourseEditor from "@/pages/tenant/CourseEditor";
import TenantMemberships from "@/pages/tenant/Memberships";
import TenantDonations from "@/pages/tenant/Donations";
import TenantPPOB from "@/pages/tenant/PPOB";
import TenantAffiliates from "@/pages/tenant/Affiliates";
import TenantSettings from "@/pages/tenant/Settings";
import TenantReports from "@/pages/tenant/Reports";
import TenantStoreBuilder from "@/pages/tenant/StoreBuilder";
import TenantDomainManager from "@/pages/tenant/DomainManager";
import TenantNotificationTemplates from "@/pages/tenant/NotificationTemplates";
import TenantCODSettings from "@/pages/tenant/CODSettings";
import TenantOnboarding from "@/pages/tenant/Onboarding";
import TenantSubscription from "@/pages/tenant/Subscription";
import TenantAccountSettings from "@/pages/tenant/AccountSettings";
import TenantCertificateSettings from "@/pages/tenant/CertificateSettings";
import TenantHighTicket from "@/pages/tenant/HighTicket";
import TenantStoreTemplates from "@/pages/tenant/StoreTemplates";
import TenantStoreTemplatesEnhanced from "@/pages/tenant/StoreTemplatesEnhanced";
import TenantStaffManagement from "@/pages/tenant/StaffManagement";
import TenantPageBuilder from "@/pages/tenant/PageBuilder";
import TenantWhitelabel from "@/pages/tenant/Whitelabel";
import TenantAdvancedReports from "@/pages/tenant/AdvancedReports";
import TenantBroadcast from "@/pages/tenant/Broadcast";
import TenantPPOBEnhanced from "@/pages/tenant/PPOBEnhanced";
import TenantHighTicketProjects from "@/pages/tenant/HighTicketProjects";
import TenantLMSEnhanced from "@/pages/tenant/LMSEnhanced";
import TenantBlog from "@/pages/tenant/Blog";
import TenantFAQ from "@/pages/tenant/FAQ";
import TenantStoreExtras from "@/pages/tenant/StoreExtras";
import TenantFinanceReports from "@/pages/tenant/FinanceReports";
import AccountingLayout from "@/pages/tenant/accounting/AccountingLayout";
import AccountingOverview from "@/pages/tenant/accounting/AccountingOverview";
import AccountingCoa from "@/pages/tenant/accounting/AccountingCoa";
import AccountingJournals from "@/pages/tenant/accounting/AccountingJournals";
import AccountingSettings from "@/pages/tenant/accounting/AccountingSettings";
import AccountingReportsLayout from "@/pages/tenant/accounting/AccountingReportsLayout";
import ReportTrialBalance from "@/pages/tenant/accounting/reports/ReportTrialBalance";
import ReportPl from "@/pages/tenant/accounting/reports/ReportPl";
import ReportBalanceSheet from "@/pages/tenant/accounting/reports/ReportBalanceSheet";
import ReportCashFlow from "@/pages/tenant/accounting/reports/ReportCashFlow";
import ReportJournalRegister from "@/pages/tenant/accounting/reports/ReportJournalRegister";
import ReportLedger from "@/pages/tenant/accounting/reports/ReportLedger";
import ReportCompare from "@/pages/tenant/accounting/reports/ReportCompare";
import TenantVouchers from "@/pages/tenant/Vouchers";
import TenantOrderBumps from "@/pages/tenant/OrderBumps";
import TenantUpsellFunnels from "@/pages/tenant/UpsellFunnels";
import TenantLoyaltySettings from "@/pages/tenant/LoyaltySettings";
import TenantLoyaltyRewards from "@/pages/tenant/LoyaltyRewards";
import TenantAnalytics from "@/pages/tenant/Analytics";
import TenantBankAccounts from "@/pages/tenant/BankAccounts";
import TenantPaymentConfirmations from "@/pages/tenant/PaymentConfirmations";
import TenantShippingSettings from "@/pages/tenant/ShippingSettings";
import TenantSaaSAffiliate from "@/pages/tenant/SaaSAffiliate";
import TenantImportExport from "@/pages/tenant/ImportExport";
import TenantSegments from "@/pages/tenant/Segments";
import TenantNotificationCenter from "@/pages/tenant/NotificationCenter";
import TenantNotificationLogs from "@/pages/tenant/NotificationLogs";
import TenantActivityLogs from "@/pages/tenant/ActivityLogs";
import TenantSupportTickets from "@/pages/tenant/SupportTickets";
import TenantSerialKeys from "@/pages/tenant/SerialKeys";
import TenantFollowUpSettings from "@/pages/tenant/FollowUpSettings";
import TenantFollowUpQueue from "@/pages/tenant/FollowUpQueue";
import TenantAbandonedCarts from "@/pages/tenant/AbandonedCarts";
import TenantLandingPages from "@/pages/tenant/LandingPages";
import TenantInventory from "@/pages/tenant/Inventory";
import TenantResellers from "@/pages/tenant/Resellers";
import TenantPOS from "@/pages/tenant/POS";
import TenantPOSSettings from "@/pages/tenant/POSSettings";
import TenantPOSShifts from "@/pages/tenant/POSShifts";
import TenantPOSReports from "@/pages/tenant/POSReports";
import TenantAPIKeys from "@/pages/tenant/APIKeys";
import TenantWebhooks from "@/pages/tenant/Webhooks";
import TenantAPIDocs from "@/pages/tenant/APIDocs";
import PublicLandingCheckout from "@/pages/public/LandingCheckout";
import PublicAPIDocs from "@/pages/public/APIDocs";
import PublicLeadForm from "@/pages/public/LeadForm";
import StoreRewards from "@/pages/public/StoreRewards";
import TenantLeads from "@/pages/tenant/Leads";
import TenantLeadSettings from "@/pages/tenant/LeadSettings";
import TenantBundles from "@/pages/tenant/Bundles";
import TenantWebinars from "@/pages/tenant/Webinars";
import TenantEvents from "@/pages/tenant/Events";
import TenantBookings from "@/pages/tenant/Bookings";
import TenantTutorial from "@/pages/tenant/Tutorial";
import PublicBundleDetail from "@/pages/public/BundleDetail";
import PublicWebinarDetail from "@/pages/public/WebinarDetail";
import PublicEventDetail from "@/pages/public/EventDetail";
import PublicBookingDetail from "@/pages/public/BookingDetail";

// Customer Pages
import CustomerDashboard from "@/pages/customer/Dashboard";
import CustomerOrders from "@/pages/customer/Orders";
import CustomerInvoices from "@/pages/customer/Invoices";
import CustomerPaymentSchedules from "@/pages/customer/PaymentSchedules";
import CustomerCourses from "@/pages/customer/Courses";
import CustomerCourseLearning from "@/pages/customer/CourseLearning";
import CustomerCertificate from "@/pages/customer/Certificate";
import CustomerMemberships from "@/pages/customer/Memberships";
import CustomerDownloads from "@/pages/customer/Downloads";
import CustomerPPOB from "@/pages/customer/PPOB";
import CustomerDonations from "@/pages/customer/Donations";
import CustomerAffiliate from "@/pages/customer/Affiliate";
import CustomerReseller from "@/pages/customer/Reseller";
import CustomerProfile from "@/pages/customer/Profile";
import CustomerAddresses from "@/pages/customer/Addresses";
import CustomerContracts from "@/pages/customer/Contracts";
import CustomerSupport from "@/pages/customer/Support";
import CustomerWishlist from "@/pages/customer/Wishlist";
import CustomerOrderTracking from "@/pages/customer/OrderTracking";
import CustomerLoyalty from "@/pages/customer/Loyalty";

function ProtectedRoute({ children, allowedRoles }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2D6CDF]" /></div>;
  if (!user) return <Navigate to="/login" />;
  if (allowedRoles && !allowedRoles.includes(user.role)) return <Navigate to="/login" />;
  return children;
}

// When on subdomain, redirect /store/:subdomain/* to clean paths
function SubdomainRedirect() {
  const location = useLocation();
  const cleanPath = location.pathname.replace(`/store/${DETECTED_SUBDOMAIN}`, '') || '/';
  return <Navigate to={cleanPath + location.search + location.hash} replace />;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Subdomain store routes — serve store directly at root paths */}
      {DETECTED_SUBDOMAIN && <>
        <Route path="/" element={<PublicStorefront />} />
        <Route path="/product/:productId" element={<PublicProductDetail />} />
        <Route path="/bundle/:bundleId" element={<PublicBundleDetail />} />
        <Route path="/checkout" element={<PublicCheckout />} />
        <Route path="/thank-you" element={<PublicThankYou />} />
        <Route path="/course/:courseId" element={<PublicCourseDetail />} />
        <Route path="/donate/:campaignId" element={<PublicDonation />} />
        <Route path="/webinar/:webinarId" element={<PublicWebinarDetail />} />
        <Route path="/event/:eventId" element={<PublicEventDetail />} />
        <Route path="/booking/:serviceId" element={<PublicBookingDetail />} />
        <Route path="/blog" element={<PublicBlog />} />
        <Route path="/blog/:slug" element={<PublicBlog />} />
        <Route path="/faq" element={<PublicFAQ />} />
        <Route path="/login" element={<StoreLogin />} />
        <Route path="/register" element={<StoreRegister />} />
        <Route path="/page/:slug" element={<PublicPage />} />
        <Route path="/lp/:slug" element={<PublicLandingCheckout />} />
        <Route path="/lead-form" element={<PublicLeadForm />} />
        <Route path="/rewards" element={<StoreRewards />} />
        <Route path={`/store/${DETECTED_SUBDOMAIN}/*`} element={<SubdomainRedirect />} />
        <Route path={`/store/${DETECTED_SUBDOMAIN}`} element={<SubdomainRedirect />} />
      </>}

      {/* Public */}
      {!DETECTED_SUBDOMAIN && <Route path="/" element={<Landing />} />}
      {!DETECTED_SUBDOMAIN && <Route path="/login" element={<Login />} />}
      {!DETECTED_SUBDOMAIN && <Route path="/register" element={<Register />} />}

      {/* Public Store */}
      <Route path="/store/:subdomain" element={<PublicStorefront />} />
      <Route path="/store/:subdomain/product/:productId" element={<PublicProductDetail />} />
      <Route path="/store/:subdomain/bundle/:bundleId" element={<PublicBundleDetail />} />
      <Route path="/store/:subdomain/checkout" element={<PublicCheckout />} />
      <Route path="/store/:subdomain/thank-you" element={<PublicThankYou />} />
      <Route path="/store/:subdomain/course/:courseId" element={<PublicCourseDetail />} />
      <Route path="/store/:subdomain/donate/:campaignId" element={<PublicDonation />} />
      <Route path="/store/:subdomain/webinar/:webinarId" element={<PublicWebinarDetail />} />
      <Route path="/store/:subdomain/event/:eventId" element={<PublicEventDetail />} />
      <Route path="/store/:subdomain/booking/:serviceId" element={<PublicBookingDetail />} />
      <Route path="/store/:subdomain/blog" element={<PublicBlog />} />
      <Route path="/store/:subdomain/blog/:slug" element={<PublicBlog />} />
      <Route path="/store/:subdomain/faq" element={<PublicFAQ />} />
      <Route path="/store/:subdomain/login" element={<StoreLogin />} />
      <Route path="/store/:subdomain/register" element={<StoreRegister />} />
      <Route path="/store/:subdomain/page/:slug" element={<PublicPage />} />
      <Route path="/store/:subdomain/lp/:slug" element={<PublicLandingCheckout />} />
      <Route path="/store/:subdomain/lead-form" element={<PublicLeadForm />} />
      <Route path="/store/:subdomain/rewards" element={<StoreRewards />} />
      <Route path="/lead-form/:tenantId" element={<PublicLeadForm />} />

      {/* Template preview (mockup renderer — works for any template without a live store) */}
      <Route path="/template-preview/:templateId" element={<TemplatePreview />} />

      {/* Public API Documentation */}
      <Route path="/api-docs" element={<PublicAPIDocs />} />

      {/* Public System Status */}
      <Route path="/status" element={<Status />} />

      {/* Public Help Center */}
      <Route path="/help" element={<Help />} />

      {/* Certificate Verification (Public) */}
      <Route path="/certificate/verify/:certificateNumber" element={<CertificateVerify />} />

      {/* Admin */}
      <Route path="/admin" element={<ProtectedRoute allowedRoles={["super_admin"]}><AdminLayout /></ProtectedRoute>}>
        <Route index element={<AdminDashboard />} />
        <Route path="tenants" element={<AdminTenants />} />
        <Route path="packages" element={<AdminPackages />} />
        <Route path="billing" element={<AdminBilling />} />
        <Route path="billing-settings" element={<AdminBillingSettings />} />
        <Route path="affiliates" element={<AdminAffiliates />} />
        <Route path="reports" element={<AdminReports />} />
        <Route path="global-settings" element={<AdminGlobalSettings />} />
        <Route path="templates" element={<AdminStoreTemplates />} />
        <Route path="notifications" element={<AdminNotificationTemplates />} />
        <Route path="wa-notifications" element={<AdminWANotifications />} />
        <Route path="module-adoption" element={<AdminModuleAdoption />} />
        <Route path="account" element={<AdminAccountSettings />} />
        <Route path="faqs" element={<AdminFAQManager />} />
        <Route path="landing-features" element={<AdminLandingFeatures />} />
        <Route path="testimonials" element={<AdminTestimonials />} />
      </Route>

      {/* Tenant Onboarding (outside layout) */}
      <Route path="/onboarding" element={<ProtectedRoute allowedRoles={["tenant", "tenant_owner", "tenant_admin"]}><TenantOnboarding /></ProtectedRoute>} />

      {/* Tenant */}
      <Route path="/dashboard" element={<ProtectedRoute allowedRoles={["tenant", "tenant_owner", "tenant_admin", "tenant_finance", "tenant_sales", "tenant_operator", "tenant_instructor", "tenant_marketing"]}><TenantLayout /></ProtectedRoute>}>
        <Route index element={<TenantDashboard />} />
        <Route path="analytics" element={<TenantAnalytics />} />
        <Route path="products" element={<TenantProducts />} />
        <Route path="orders" element={<TenantOrders />} />
        <Route path="customers" element={<TenantCustomers />} />
        <Route path="invoices" element={<TenantInvoices />} />
        <Route path="payment-plans" element={<TenantPaymentPlans />} />
        <Route path="courses" element={<TenantCourses />} />
        <Route path="courses/:courseId" element={<TenantCourseEditor />} />
        <Route path="memberships" element={<TenantMemberships />} />
        <Route path="donations" element={<TenantDonations />} />
        <Route path="ppob" element={<TenantPPOB />} />
        <Route path="high-ticket" element={<TenantHighTicket />} />
        <Route path="affiliates" element={<TenantAffiliates />} />
        <Route path="resellers" element={<TenantResellers />} />
        <Route path="settings" element={<TenantSettings />} />
        <Route path="reports" element={<TenantReports />} />
        <Route path="store-builder" element={<TenantStoreBuilder />} />
        <Route path="domains" element={<TenantDomainManager />} />
        <Route path="notifications" element={<TenantNotificationTemplates />} />
        <Route path="cod" element={<TenantCODSettings />} />
        <Route path="subscription" element={<TenantSubscription />} />
        <Route path="account" element={<TenantAccountSettings />} />
        <Route path="certificates" element={<TenantCertificateSettings />} />
        <Route path="templates" element={<TenantStoreTemplates />} />
        <Route path="templates-enhanced" element={<TenantStoreTemplatesEnhanced />} />
        <Route path="staff" element={<TenantStaffManagement />} />
        <Route path="page-builder" element={<TenantPageBuilder />} />
        <Route path="whitelabel" element={<TenantWhitelabel />} />
        <Route path="advanced-reports" element={<TenantAdvancedReports />} />
        <Route path="broadcast" element={<TenantBroadcast />} />
        <Route path="ppob-enhanced" element={<TenantPPOBEnhanced />} />
        <Route path="high-ticket-projects" element={<TenantHighTicketProjects />} />
        <Route path="lms-enhanced" element={<TenantLMSEnhanced />} />
        <Route path="blog" element={<TenantBlog />} />
        <Route path="faq" element={<TenantFAQ />} />
        <Route path="store-extras" element={<TenantStoreExtras />} />
        <Route path="finance-reports" element={<TenantFinanceReports />} />
        <Route path="accounting" element={<AccountingLayout />}>
          <Route index element={<AccountingOverview />} />
          <Route path="coa" element={<AccountingCoa />} />
          <Route path="journals" element={<AccountingJournals />} />
          <Route path="settings" element={<AccountingSettings />} />
          <Route path="reports" element={<AccountingReportsLayout />}>
            <Route index element={<Navigate to="trial-balance" replace />} />
            <Route path="trial-balance" element={<ReportTrialBalance />} />
            <Route path="pl" element={<ReportPl />} />
            <Route path="balance-sheet" element={<ReportBalanceSheet />} />
            <Route path="cash-flow" element={<ReportCashFlow />} />
            <Route path="journal-register" element={<ReportJournalRegister />} />
            <Route path="ledger" element={<ReportLedger />} />
            <Route path="compare" element={<ReportCompare />} />
          </Route>
        </Route>
        <Route path="vouchers" element={<TenantVouchers />} />
        <Route path="order-bumps" element={<TenantOrderBumps />} />
        <Route path="upsell-funnels" element={<TenantUpsellFunnels />} />
        <Route path="loyalty-settings" element={<TenantLoyaltySettings />} />
        <Route path="loyalty-rewards" element={<TenantLoyaltyRewards />} />
        <Route path="bundles" element={<TenantBundles />} />
        <Route path="webinars" element={<TenantWebinars />} />
        <Route path="events" element={<TenantEvents />} />
        <Route path="bookings" element={<TenantBookings />} />
        <Route path="shipping" element={<TenantShippingSettings />} />
        <Route path="saas-affiliate" element={<TenantSaaSAffiliate />} />
        <Route path="import-export" element={<TenantImportExport />} />
        <Route path="segments" element={<TenantSegments />} />
        <Route path="notification-center" element={<TenantNotificationCenter />} />
        <Route path="notification-logs" element={<TenantNotificationLogs />} />
        <Route path="activity-logs" element={<TenantActivityLogs />} />
        <Route path="support-tickets" element={<TenantSupportTickets />} />
        <Route path="serial-keys" element={<TenantSerialKeys />} />
        <Route path="followup-settings" element={<TenantFollowUpSettings />} />
        <Route path="followup" element={<TenantFollowUpQueue />} />
        <Route path="abandoned-carts" element={<TenantAbandonedCarts />} />
        <Route path="landing-pages" element={<TenantLandingPages />} />
        <Route path="inventory" element={<TenantInventory />} />
        <Route path="pos" element={<TenantPOS />} />
        <Route path="pos-settings" element={<TenantPOSSettings />} />
        <Route path="pos-shifts" element={<TenantPOSShifts />} />
        <Route path="pos-reports" element={<TenantPOSReports />} />
        <Route path="api-keys" element={<TenantAPIKeys />} />
        <Route path="webhooks" element={<TenantWebhooks />} />
        <Route path="api-docs" element={<TenantAPIDocs />} />
        <Route path="leads" element={<TenantLeads />} />
        <Route path="lead-settings" element={<TenantLeadSettings />} />
        <Route path="bank-accounts" element={<TenantBankAccounts />} />
        <Route path="payment-confirmations" element={<TenantPaymentConfirmations />} />
        <Route path="tutorial" element={<TenantTutorial />} />
      </Route>

      {/* Customer */}
      <Route path="/my" element={<ProtectedRoute allowedRoles={["customer"]}><CustomerLayout /></ProtectedRoute>}>
        <Route index element={<CustomerDashboard />} />
        <Route path="profile" element={<CustomerProfile />} />
        <Route path="addresses" element={<CustomerAddresses />} />
        <Route path="orders" element={<CustomerOrders />} />
        <Route path="orders/:orderId/tracking" element={<CustomerOrderTracking />} />
        <Route path="invoices" element={<CustomerInvoices />} />
        <Route path="payment-schedules" element={<CustomerPaymentSchedules />} />
        <Route path="courses" element={<CustomerCourses />} />
        <Route path="courses/:courseId" element={<CustomerCourseLearning />} />
        <Route path="downloads" element={<CustomerDownloads />} />
        <Route path="certificates" element={<CustomerCourses />} />
        <Route path="certificates/:certificateId" element={<CustomerCertificate />} />
        <Route path="memberships" element={<CustomerMemberships />} />
        <Route path="affiliate" element={<CustomerAffiliate />} />
        <Route path="reseller" element={<CustomerReseller />} />
        <Route path="ppob" element={<CustomerPPOB />} />
        <Route path="donations" element={<CustomerDonations />} />
        <Route path="contracts" element={<CustomerContracts />} />
        <Route path="support" element={<CustomerSupport />} />
        <Route path="wishlist" element={<CustomerWishlist />} />
        <Route path="loyalty" element={<CustomerLoyalty />} />
      </Route>

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <PlatformBrandingEffect enabled={!DETECTED_SUBDOMAIN} />
        <AppRoutes />
        <Toaster position="top-right" />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
